import React, { useEffect } from 'react';

const BridgePattern = () => {
  useEffect(() => {
    // SEO optimization
    document.title = 'Bridge Pattern - Structural Design Pattern Guide 2024';
    
    // Add meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Master the Bridge Pattern - separate abstraction from implementation for platform independence. Learn with remote control and drawing API examples.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Master the Bridge Pattern - separate abstraction from implementation for platform independence. Learn with remote control and drawing API examples.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    // Add keywords meta tag
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'bridge pattern, design patterns, structural patterns, abstraction implementation, platform independence, software design');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = 'bridge pattern, design patterns, structural patterns, abstraction implementation, platform independence, software design';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    return () => {
      // Cleanup - restore original title
      document.title = 'SlazySloth - Deep Dives';
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      margin: 0,
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      lineHeight: 1.6,
      color: '#333',
      background: '#ffffff',
      fontSize: '16px'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '16px',
      color: '#333',
      marginBottom: '1rem',
      fontWeight: 400
    },
    content: {
      fontSize: '16px',
      lineHeight: 1.6
    },
    heading: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      margin: '1.5rem 0 1rem 0'
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    list: {
      marginBottom: '1rem',
      paddingLeft: '1.5rem'
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    code: {
      background: '#f5f5f5',
      padding: '0.2rem 0.4rem',
      fontFamily: 'monospace',
      fontSize: '16px',
      color: '#333'
    },
    codeBlock: {
      background: '#f5f5f5',
      padding: '1rem',
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#333',
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
      overflow: 'auto'
    },
    blockquote: {
      borderLeft: '4px solid #008080',
      paddingLeft: '1rem',
      margin: '1rem 0',
      fontStyle: 'italic',
      color: '#555'
    }
  };

  return (
    <article style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Bridge Pattern</h1>
        <p style={styles.subtitle}>
          Structural Design Pattern - Separate abstraction from implementation
        </p>
      </header>

      <main style={styles.content}>
        <section>
          <h2 style={styles.heading}>Intent</h2>
          <p style={styles.paragraph}>
            The Bridge pattern separates an abstraction from its implementation so that both can vary 
            independently. It creates a bridge between the abstraction hierarchy and the implementor 
            hierarchy, allowing you to change either without affecting the other.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Problem</h2>
          <p style={styles.paragraph}>
            When you have a class hierarchy that needs to work with multiple implementations, 
            traditional inheritance creates a rigid structure. The Bridge pattern provides 
            flexibility by separating what you want to do (abstraction) from how you do it (implementation).
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Real-World Example: Remote Control System</h2>
          <div style={styles.codeBlock}>
{`// Implementation interface (Bridge)
interface Device {
    boolean isEnabled();
    void enable();
    void disable();
    int getVolume();
    void setVolume(int volume);
    int getChannel();
    void setChannel(int channel);
    void printStatus();
}

// Concrete implementations
class Television implements Device {
    private boolean on = false;
    private int volume = 30;
    private int channel = 1;
    private String brand;
    
    public Television(String brand) {
        this.brand = brand;
    }
    
    @Override
    public boolean isEnabled() {
        return on;
    }
    
    @Override
    public void enable() {
        on = true;
        System.out.println(brand + " TV is now ON");
    }
    
    @Override
    public void disable() {
        on = false;
        System.out.println(brand + " TV is now OFF");
    }
    
    @Override
    public int getVolume() {
        return volume;
    }
    
    @Override
    public void setVolume(int volume) {
        this.volume = Math.max(0, Math.min(100, volume));
        System.out.println(brand + " TV volume set to " + this.volume);
    }
    
    @Override
    public int getChannel() {
        return channel;
    }
    
    @Override
    public void setChannel(int channel) {
        this.channel = Math.max(1, Math.min(999, channel));
        System.out.println(brand + " TV channel set to " + this.channel);
    }
    
    @Override
    public void printStatus() {
        System.out.println("------------------------------------");
        System.out.println("| " + brand + " Television");
        System.out.println("| Power: " + (on ? "ON" : "OFF"));
        System.out.println("| Volume: " + volume + "%");
        System.out.println("| Channel: " + channel);
        System.out.println("------------------------------------");
    }
}

class Radio implements Device {
    private boolean on = false;
    private int volume = 50;
    private int channel = 1; // Radio station
    private String type;
    
    public Radio(String type) {
        this.type = type;
    }
    
    @Override
    public boolean isEnabled() {
        return on;
    }
    
    @Override
    public void enable() {
        on = true;
        System.out.println(type + " Radio is now ON");
    }
    
    @Override
    public void disable() {
        on = false;
        System.out.println(type + " Radio is now OFF");
    }
    
    @Override
    public int getVolume() {
        return volume;
    }
    
    @Override
    public void setVolume(int volume) {
        this.volume = Math.max(0, Math.min(100, volume));
        System.out.println(type + " Radio volume set to " + this.volume);
    }
    
    @Override
    public int getChannel() {
        return channel;
    }
    
    @Override
    public void setChannel(int channel) {
        this.channel = Math.max(1, Math.min(200, channel));
        System.out.println(type + " Radio tuned to station " + this.channel);
    }
    
    @Override
    public void printStatus() {
        System.out.println("------------------------------------");
        System.out.println("| " + type + " Radio");
        System.out.println("| Power: " + (on ? "ON" : "OFF"));
        System.out.println("| Volume: " + volume + "%");
        System.out.println("| Station: " + channel);
        System.out.println("------------------------------------");
    }
}

// Abstraction (Remote Control)
class RemoteControl {
    protected Device device;
    
    public RemoteControl(Device device) {
        this.device = device;
    }
    
    public void togglePower() {
        System.out.println("Remote: Toggling power");
        if (device.isEnabled()) {
            device.disable();
        } else {
            device.enable();
        }
    }
    
    public void volumeDown() {
        System.out.println("Remote: Volume down");
        device.setVolume(device.getVolume() - 10);
    }
    
    public void volumeUp() {
        System.out.println("Remote: Volume up");
        device.setVolume(device.getVolume() + 10);
    }
    
    public void channelDown() {
        System.out.println("Remote: Channel down");
        device.setChannel(device.getChannel() - 1);
    }
    
    public void channelUp() {
        System.out.println("Remote: Channel up");
        device.setChannel(device.getChannel() + 1);
    }
    
    public void showStatus() {
        device.printStatus();
    }
}

// Refined abstraction
class AdvancedRemoteControl extends RemoteControl {
    
    public AdvancedRemoteControl(Device device) {
        super(device);
    }
    
    public void mute() {
        System.out.println("Advanced Remote: Muting device");
        device.setVolume(0);
    }
    
    public void setVolume(int volume) {
        System.out.println("Advanced Remote: Setting volume to " + volume);
        device.setVolume(volume);
    }
    
    public void goToChannel(int channel) {
        System.out.println("Advanced Remote: Going to channel " + channel);
        device.setChannel(channel);
    }
    
    public void powerOnWithChannel(int channel) {
        System.out.println("Advanced Remote: Power on with specific channel");
        if (!device.isEnabled()) {
            device.enable();
        }
        device.setChannel(channel);
    }
    
    public void powerOnWithVolumeAndChannel(int volume, int channel) {
        System.out.println("Advanced Remote: Power on with volume " + volume + " and channel " + channel);
        if (!device.isEnabled()) {
            device.enable();
        }
        device.setVolume(volume);
        device.setChannel(channel);
    }
}

// Voice-controlled remote (another refined abstraction)
class VoiceRemoteControl extends RemoteControl {
    
    public VoiceRemoteControl(Device device) {
        super(device);
    }
    
    public void voiceCommand(String command) {
        System.out.println("Voice Remote: Processing command '" + command + "'");
        
        command = command.toLowerCase().trim();
        
        if (command.contains("turn on") || command.contains("power on")) {
            if (!device.isEnabled()) {
                device.enable();
            }
        } else if (command.contains("turn off") || command.contains("power off")) {
            if (device.isEnabled()) {
                device.disable();
            }
        } else if (command.contains("volume up")) {
            device.setVolume(device.getVolume() + 10);
        } else if (command.contains("volume down")) {
            device.setVolume(device.getVolume() - 10);
        } else if (command.contains("channel")) {
            // Extract channel number from command
            String[] words = command.split(" ");
            for (int i = 0; i < words.length - 1; i++) {
                if (words[i].equals("channel")) {
                    try {
                        int channel = Integer.parseInt(words[i + 1]);
                        device.setChannel(channel);
                        break;
                    } catch (NumberFormatException e) {
                        System.out.println("Voice Remote: Could not understand channel number");
                    }
                }
            }
        } else if (command.contains("status")) {
            device.printStatus();
        } else {
            System.out.println("Voice Remote: Command not recognized");
        }
    }
}

// Usage
public class BridgeDemo {
    public static void main(String[] args) {
        System.out.println("=== Bridge Pattern Demo ===\\n");
        
        // Create devices (implementations)
        Device samsungTV = new Television("Samsung");
        Device sonyRadio = new Radio("Sony");
        
        System.out.println("1. Basic Remote Control with TV");
        RemoteControl basicRemote = new RemoteControl(samsungTV);
        basicRemote.togglePower();
        basicRemote.volumeUp();
        basicRemote.channelUp();
        basicRemote.showStatus();
        
        System.out.println("\\n2. Advanced Remote Control with same TV");
        AdvancedRemoteControl advancedRemote = new AdvancedRemoteControl(samsungTV);
        advancedRemote.powerOnWithVolumeAndChannel(75, 25);
        advancedRemote.showStatus();
        
        System.out.println("\\n3. Same Advanced Remote with Radio");
        advancedRemote = new AdvancedRemoteControl(sonyRadio);
        advancedRemote.powerOnWithChannel(101);
        advancedRemote.setVolume(60);
        advancedRemote.showStatus();
        
        System.out.println("\\n4. Voice Remote Control with TV");
        VoiceRemoteControl voiceRemote = new VoiceRemoteControl(samsungTV);
        voiceRemote.voiceCommand("turn on");
        voiceRemote.voiceCommand("volume up");
        voiceRemote.voiceCommand("channel 15");
        voiceRemote.voiceCommand("status");
        
        System.out.println("\\n5. Voice Remote Control with Radio");
        voiceRemote = new VoiceRemoteControl(sonyRadio);
        voiceRemote.voiceCommand("power on");
        voiceRemote.voiceCommand("channel 95");
        voiceRemote.voiceCommand("volume down");
        voiceRemote.voiceCommand("status");
        
        System.out.println("\\n=== Demonstrating Bridge Benefits ===");
        System.out.println("- Same remote controls work with different devices");
        System.out.println("- Can add new device types without changing remotes");
        System.out.println("- Can add new remote types without changing devices");
        System.out.println("- Abstraction and implementation vary independently");
    }
}`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>Key Benefits</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Independence:</strong> Abstraction and implementation can vary independently</li>
            <li style={styles.listItem}><strong>Platform Independence:</strong> Same abstraction works across different platforms</li>
            <li style={styles.listItem}><strong>Extensibility:</strong> Easy to add new abstractions or implementations</li>
            <li style={styles.listItem}><strong>Runtime Binding:</strong> Can switch implementations at runtime</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>When to Use</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}>When you want to avoid permanent binding between abstraction and implementation</li>
            <li style={styles.listItem}>When you need to share implementations among multiple objects</li>
            <li style={styles.listItem}>When you want to extend abstractions and implementations independently</li>
            <li style={styles.listItem}>When you need to switch implementations at runtime</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Conclusion</h2>
          <p style={styles.paragraph}>
            The Bridge pattern is essential for creating flexible, platform-independent code. 
            It's commonly used in GUI frameworks, device drivers, and any system where you 
            need to support multiple implementations of the same interface.
          </p>
        </section>
      </main>
    </article>
  );
};

export default BridgePattern;
